<template>
  <NuxtLayout name="order">
    <OrganismsDialogsMessage :show="cartItemIdToRemove" icon="pozor" :text="$t('Opravdu chcete položku z košíku odstranit?')" type="bg-error" :confirm="true" @result="itemRemovalConfirmed($event)" />
    <OrganismsListsOrderTabs :selected-tab-index="0" :max-enabled-index="1" />
    <div class="cart">

      <h1>{{ $t('Obsah košíku') }}</h1>

      <MoleculesBlocksCartItem v-for="item in cartItems" :imageId="item.imageId" :name="item.name" :variant-name="item.variantName" :code="item.code" :url="item.url" :description="item.description" :dispatchText="item.dispatchText" :piecePriceFormatted="item.piecePriceFormatted" :totalPriceFormatted="item.totalPriceFormatted" :unit="item.unit" :service-info="item.serviceInfo" v-model="item.quantity" @update:modelValue="(...args) => updateCartItem(item.cartItemId, ...args)" @remove="removeCartItem(item.cartItemId)" />

      <div class="flex flex-wrap items-end justify-between gap-8 my-12">
        <div class="flex-shrink">
          <strong class="block">{{ $t('Slevový kód') }}:</strong>
          <template v-if="!cart.prices.value.couponSale.valueWithoutVat">
            <div class="my-2">
              <AtomsFormsTextInput v-model="coupon" class="min-w-[250px] lg:min-w-[300px]" @enter="checkCoupon" />
              <MoleculesButtonsButton v-if="coupon" xsmall="true" :dummy="true" @click="checkCoupon" class="ml-2">{{ $t('Uplatnit kupón') }}</MoleculesButtonsButton>
            </div>
          </template>
          <template v-else>
            <div class="my-4 flex items-center gap-4">
              <AtomsImagesIcon icon="inf" :filled="true" />
              <div>
                <span class="mr-4">{{ $t('Uplatněna sleva ve výši') }} {{ cart.prices.value.couponSale.formattedValue }}</span>
                <button class="text-conversion uppercase hover:underline" @click="removeCoupon">zrušit</button>
              </div>
            </div>
          </template>
        </div>
        <div class="text-right w-full lg:w-auto">
          {{ $t('Cena celkem') }}: <strong class="ml-10 text-lg">{{ cart.prices.value.itemsTotal.formattedValue }}</strong>
        </div>
      </div>
      <div v-if="cartItemsHaveServices" class="font-bold text-center">
      {{ $t('Upozornění: V košíku máte zboží, u kterého máte vybranou službu rytí/gravírování. Takové zboží není možné vrátit ani vyměnit.') }}
      </div>
      <div class="flex items-center justify-between gap-8 my-12">
        <div class="flex-1">
          <AtomsGlobalLink to="/">
            <AtomsImagesIcon icon="sipkaDoleva" size="xs" class="mr-2" /> {{ $t('zpět do eshopu') }}
          </AtomsGlobalLink>
        </div>
        <div>
          <MoleculesButtonsButton :href="appConfig.definedPageUrls.order" :arrowRight="true">{{ $t('Přejít k nákupu') }}</MoleculesButtonsButton>
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>
<script setup>

const appConfig = useAppConfig();
const message = useMessage();
const i18n = useI18n();
const locale = useLocale();
const cart = useCart();
const analytics = useAnalytics();

const cartItems = ref(cart.presentation);
const cartItemIdToRemove = ref(null);
const coupon = ref('');

const updateCartItem = (cartItemId, quantity) => {

  if (quantity < 1) {
    cartItemIdToRemove.value = cartItemId;
    return;
  }

  // update view model
  cartItems.value.find(q => q.cartItemId === cartItemId).quantity = quantity;

  // update cart
  submitUpdateCartItem(cartItemId, quantity);
}

const submitUpdateCartItem = useDebounceFn(async (cartItemId, quantity) => {

  await cart.update(cartItemId, quantity);
  message.show(i18n.t('Obsah košíku byl upraven'), null, false, false, i18n.t('Dobře'), 3);

}, 500);

const removeCartItem = (cartItemId) => cartItemIdToRemove.value = cartItemId;

const itemRemovalConfirmed = async result => {
  const cartItemId = cartItemIdToRemove.value;
  cartItemIdToRemove.value = null;

  if (result) {

    // update cart
    await cart.remove(cartItemId);

    if (cartItems.value.length === 0) {
      useRouter().push(appConfig.definedPageUrls.emptyCart);
    }
    else {
      message.show(i18n.t('Položka byla z košíku odstraněna'), null, false, false, i18n.t('Dobře'), 3);
    }
  }
  else {
    const cartItem = cartItems.value.find(q => q.cartItemId === cartItemId);

    if (cartItem.quantity === 0) {
      cartItem.quantity = 1;
    }
  }

}

const checkCoupon = async () => {
  const couponInfo = (await useApiFetch(`/api/coupon?code=${coupon.value}&currencyId=${locale.getLocale().currencyId}&languageId=${locale.getLocale().languageId}`)).data.value.coupon;

  if (couponInfo.state === 'VALID') {
    useShopCookie().set('coupon', coupon.value);
    
    await cart.updatePresentation();
    message.show(i18n.t('Slevový kód byl aktivován'), null, false, false, i18n.t('Dobře'), 3);
    coupon.value = '';

  }
  else if (couponInfo.state === 'COUPONUSED') {
    message.show(i18n.t('Zadaný kód již byl použit'), null, true, false, i18n.t('Dobře'), 3);
  }
  else if (couponInfo.state === 'CAMPAIGNEXPIRED' || couponInfo.state === 'COUPONEXPIRED') {
    message.show(i18n.t('Platnost zadaného kódu již vypršela'), null, true, false, i18n.t('Dobře'), 3);
  }
  else {
    message.show(i18n.t('Zadaný kód není platný'), null, true, false, i18n.t('Dobře'), 3);
  }

}

const removeCoupon = async() => {
  useShopCookie().remove('coupon');
  await cart.updatePresentation();
  message.show(i18n.t('Slevový kód byl zrušen'), null, false, false, i18n.t('Dobře'), 3);
}

const cartItemsHaveServices = computed(() => {

  const result = cart.items.value.filter(q => q.s || q.set?.filter(qq => qq.s).length).length;

  return result;
});

analytics.pushEvent('view_cart', {
  ecommerce: {
    value: cart.prices.value.itemsTotal.valueWithoutVat,
    items: cart.presentation.value.map(item => item.dataLayerItem)
  }
});

</script>
<style lang="postcss">
.cart {

  h1 {
    @apply py-8 text-2xl lg:text-3xl font-bold font-secondary;
  }
}
</style>